<template>
	<ContainerCenter>
		<v-card>
			<v-toolbar
				color="primary"
				dark
				flat
			>
				<v-toolbar-title>Olá, {{ usuario.nomeCompleto }} </v-toolbar-title>
			</v-toolbar>
			<v-card-text v-if="authModule.emailPendente && !editando">
				<div class="body-1 text-center">
					Enviamos um link de confirmação para <strong>{{ authModule.emailPendente }}</strong>.
					<br> Depois de clicar nele, você também pode entrar com esse e-mail e a sua senha.
					<br> Confira também a caixa de spam.
				</div>
			</v-card-text>
			<v-card-text v-else>
				<v-form
					ref="form"
					@submit.prevent="enviar"
					id="form-associar-email"
				>
					<div class="body-1 text-center mb-2">
						Cadastre seu e-mail para recuperar o acesso se um dia esquecer a senha.
						Depois de confirmá-lo pelo link que vamos enviar, você entra com o CIM ou com o e-mail, usando a mesma senha.
						<br> Por segurança, informe também a sua senha atual: assim sabemos que é você.
					</div>
					<v-text-field
						autofocus
						name="email"
						label="E-mail"
						type="email"
						v-model="email"
						autocomplete="email"
						:rules="[ obrigatorio, emailValido ]"
					/>
					<v-text-field
						name="senha"
						label="Senha atual"
						:type="verSenha ? 'text' : 'password'"
						v-model="senha"
						:append-icon="verSenha ? 'mdi-eye-off' : 'mdi-eye'"
						@click:append="() => verSenha = !verSenha"
						:rules="[ obrigatorio ]"
						autocomplete="current-password"
					/>
				</v-form>
			</v-card-text>
			<v-card-actions v-if="authModule.emailPendente && !editando" class="flex-column">
				<v-btn
					block
					color="primary"
					@click="continuar"
				> Continuar </v-btn>
				<v-btn
					block
					text
					class="mt-2 ml-0"
					@click="editando = true"
				> Reenviar ou trocar e-mail </v-btn>
			</v-card-actions>
			<v-card-actions v-else class="flex-column">
				<v-btn
					block
					color="primary"
					type="submit"
					form="form-associar-email"
					:disabled="carregando"
					:loading="carregando"
				> Enviar link </v-btn>
				<v-btn
					v-if="authModule.emailPendente"
					block
					text
					class="mt-2 ml-0"
					@click="editando = false"
				> Cancelar </v-btn>
				<v-btn
					v-else
					block
					text
					class="mt-2 ml-0"
					@click="continuar"
				> Agora não </v-btn>
			</v-card-actions>
		</v-card>
	</ContainerCenter>
</template>

<script setup lang="ts">
import { PropType, ref } from 'vue'
import ContainerCenter from '@/components/ui/ContainerCenter.vue'
import { emailValido, obrigatorio } from '@/shareds/regras'
import { AcessoParcial } from 'common/types/Login'
import { associarEmail } from '@/services/AuthService'
import { alert, authModule } from '@/store'
import { auth } from '@/firebase'
import { useRouter } from '@/composables/router'

const props = defineProps({
	usuario: { type: Object as PropType<Pick<AcessoParcial, 'nomeCompleto' | 'email'>>, required: true },
})

const router = useRouter()

const form = ref<HTMLFormElement & {
	validate: () => boolean,
}>()

const carregando = ref(false)
const editando = ref(false)
const verSenha = ref(false)
const email = ref(authModule.emailPendente ?? props.usuario.email ?? '')
const senha = ref('')

async function enviar() {
	if (!form.value!.validate()) return
	try {
		carregando.value = true
		const { data } = await associarEmail({
			email: email.value.trim(),
			senha: senha.value,
		})
		// antes do logarToken, que troca a sessão do Firebase pela anônima
		const confirmacaoEnviada = await enviarConfirmacao(data.emailPendente!, senha.value)
		await authModule.logarToken(data)
		editando.value = false
		senha.value = ''
		if (!confirmacaoEnviada)
			alert.setError('Não foi possível enviar o e-mail de confirmação. Use "Reenviar ou trocar e-mail".')
	} catch (error) {
		alert.setError(error)
	} finally {
		carregando.value = false
	}
}

/** o Firebase só envia a confirmação na sessão do próprio usuário, então o app entra com a senha que acabou de ser conferida */
async function enviarConfirmacao(emailAssociado: string, senhaAtual: string) {
	try {
		auth.languageCode = 'pt_BR'
		const { user } = await auth.signInWithEmailAndPassword(emailAssociado, senhaAtual)
		await user!.sendEmailVerification()
		return true
	} catch (error) {
		console.error(error)
		return false
	}
}

function continuar() {
	router.push({ name: 'home' })
		.catch(() => {})
}
</script>
